import { useMemo, useState, useCallback } from 'react';
import { FieldMask } from '@weave/schema-gen-ts/dist/google/protobuf/field_mask.pb';
import { CampaignType_Enum as CampaignType } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2';
import { List, ListListsRequest } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { useCreateList, useDeleteList, useUpdateList, useListLists } from '@frontend/api-bulk-messaging';

export const useContactLists = (accessibleLocationIds: string[], campaignType: CampaignType) => {
  const [searchQuery, setSearchQuery] = useState('');
  const listListsRequest: ListListsRequest = {
    locationId: accessibleLocationIds[0],
    pageSize: 25,
    searchQuery,
    types: [campaignType],
  };

  const queryResult = useListLists(listListsRequest);
  const { mutateAsync: createList } = useCreateList(listListsRequest);
  const { mutateAsync: deleteList } = useDeleteList(listListsRequest);
  const { mutateAsync: updateList } = useUpdateList(listListsRequest);

  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const contactLists = useMemo(() => queryResult.data?.pages.flatMap((page) => page.lists) ?? [], [queryResult.data]);

  const selectContactList = useCallback((listId: string) => {
    setSelectedListIds((prevIds) => [...prevIds, listId]);
  }, []);

  const deselectContactList = useCallback((listId: string) => {
    setSelectedListIds((prevIds) => prevIds.filter((selectedListId) => selectedListId !== listId));
  }, []);

  const createContactList = useCallback(
    async (list: List) => {
      const response = await createList({ list });
      if (response.listId) selectContactList(response.listId);
    },
    [createList, selectContactList]
  );

  const deleteContactList = useCallback(
    async (listId: string) => {
      await deleteList({ listId });
      deselectContactList(listId);
    },
    [deleteList, deselectContactList]
  );

  const renameContactList = useCallback(
    async (list: List) => {
      await updateList({ list, updateMask: new FieldMask({ paths: ['list.name'] }) });
    },
    [updateList]
  );

  return {
    contactLists,
    contactListsQueryResult: queryResult,
    createContactList,
    deleteContactList,
    deselectContactList,
    renameContactList,
    searchQuery,
    selectContactList,
    selectedContactLists: selectedListIds,
    setSelectedContactLists: setSelectedListIds,
    setSearchQuery,
  };
};
